import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  map, take
} from 'rxjs/operators';

import type { Favorite, StoredFavorite, StoredFavoritePositions } from 'models/favorites.model';
import { StoreService } from 'shared/services/store.service';

const STORE_KEY = 'favorites';

@Injectable({
  providedIn: 'root'
})
export class FavoriteStoreService {
  constructor(
    private storeService: StoreService
  ) {
    //
  }

  resetFavorites(): void {
    this.storeService.resetData({}, STORE_KEY);
  }

  getFavorites(): Observable<StoredFavoritePositions> {
    return this.storeService.getData(STORE_KEY).pipe(
      map((favorites) => favorites || {})
    );
  }

  getFavorite(lineId: string): Observable<StoredFavorite | undefined> {
    return this.getFavorites().pipe(
      take(1),
      map((favorites) => (favorites && Object.prototype.hasOwnProperty.call(favorites, lineId)
        ? favorites[lineId]
        : undefined))
    );
  }

  addFavorite(
    { documentId, lineId, title }: Pick<Favorite, 'documentId' | 'lineId' | 'title'>
  ): Observable<StoredFavoritePositions> {
    return this.getFavorites().pipe(
      take(1),
      map((favorites) => {
        if (favorites && !Object.prototype.hasOwnProperty.call(favorites, lineId)) {
          const newFavorite = { [lineId]: { documentId, title: title || '' } };
          this.storeService.setData(newFavorite, STORE_KEY);

          return { ...favorites, ...newFavorite };
        }

        return favorites;
      })
    );
  }

  setFavorites(favorites: Favorite[]): void {
    const storedFavorites: StoredFavoritePositions = {};

    favorites.forEach((favorite) => {
      storedFavorites[favorite.lineId] = {
        documentId: favorite.documentId,
        title: favorite.title || ''
      };
    });

    this.resetFavorites();
    this.storeService.setData(storedFavorites, STORE_KEY);
  }

  removeFavorite(
    { documentId, lineId }: Pick<Partial<Favorite>, 'documentId' | 'lineId'>
  ): Observable<StoredFavoritePositions> {
    return !lineId && !documentId
      ? of({})
      : this.getFavorites()
        .pipe(
          take(1),
          map((favorites) => {
            const updatedFavorites: StoredFavoritePositions = { ...(favorites || {}) };

            // remove the line
            if (lineId) {
              if (Object.prototype.hasOwnProperty.call(updatedFavorites, lineId)) {
                delete updatedFavorites[lineId];
              }
            }

            // remove all favorites that no longer have a quote now
            if (documentId) {
              Object.keys(updatedFavorites).forEach((favLineId) => {
                if (updatedFavorites[favLineId].documentId === documentId) {
                  delete updatedFavorites[favLineId];
                }
              });
            }

            this.resetFavorites();
            this.storeService.setData(updatedFavorites, STORE_KEY);

            return updatedFavorites;
          })
        );
  }

  updateTitle(favorite: Favorite, title: string): Observable<StoredFavoritePositions> {
    const { lineId } = favorite;

    return this.getFavorites()
      .pipe(
        take(1),
        map((favorites) => {
          if (favorites && Object.prototype.hasOwnProperty.call(favorites, lineId)) {
            const updatedFavorite = {
              [lineId]: {
                ...favorites[lineId],
                title: title || ''
              }
            };
            this.storeService.setData(updatedFavorite, STORE_KEY);

            return { ...favorites, ...updatedFavorite };
          }

          return favorites;
        })
      );
  }
}
